import apiService from '../services/apiService';
import TimeEntry from '../models/Timeentry';

export const timeEntryController = {
  // Get all time entries
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `time-entries?${queryParams}` : 'time-entries';
      const response = await apiService.get(endpoint);
      const items = response.data?.map(item => TimeEntry.fromJSON(item)) || [];
      return { success: true, data: items, total: response.total || items.length };
    } catch (error) {
      return { success: false, data: [], message: error.message || 'Failed to fetch time entries' };
    }
  },

  // Get single time entry by ID
  getById: async (id) => {
    try {
      const response = await apiService.get(`time-entries/${id}`);
      return { success: true, data: TimeEntry.fromJSON(response.data) };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Get time entries by user
  getByUser: async (userId) => {
    try {
      const response = await apiService.get(`time-entries?userId=${userId}`);
      const items = response.data?.map(item => TimeEntry.fromJSON(item)) || [];
      return { success: true, data: items };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // Get time entries by project
  getByProject: async (projectId) => {
    try {
      const response = await apiService.get(`time-entries?projectId=${projectId}`);
      const items = response.data?.map(item => TimeEntry.fromJSON(item)) || [];
      return { success: true, data: items };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // Create new time entry
  create: async (data) => {
    try {
      const item = new TimeEntry(data);
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.post('time-entries', item.toJSON());
      return { 
        success: true, 
        data: TimeEntry.fromJSON(response.data), 
        message: 'Time entry created successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update existing time entry
  update: async (id, data) => {
    try {
      const item = new TimeEntry({ ...data, id });
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.put(`time-entries/${id}`, item.toJSON());
      return { 
        success: true, 
        data: TimeEntry.fromJSON(response.data), 
        message: 'Time entry updated successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update time entry status
  updateStatus: async (id, status) => {
    try {
      const response = await apiService.patch(`time-entries/${id}/status`, { status });
      return { 
        success: true, 
        data: TimeEntry.fromJSON(response.data), 
        message: 'Status updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Start timer
  startTimer: async (data) => {
    try {
      const entry = new TimeEntry({
        ...data,
        startTime: new Date().toTimeString().slice(0, 5),
        date: new Date().toISOString().split('T')[0]
      });
      const response = await apiService.post('time-entries/start', entry.toJSON());
      return { 
        success: true, 
        data: TimeEntry.fromJSON(response.data), 
        message: 'Timer started' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Stop timer
  stopTimer: async (id) => {
    try {
      const response = await apiService.patch(`time-entries/${id}/stop`, {
        endTime: new Date().toTimeString().slice(0, 5)
      });
      return { 
        success: true, 
        data: TimeEntry.fromJSON(response.data), 
        message: 'Timer stopped' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Delete time entry
  delete: async (id) => {
    try {
      await apiService.delete(`time-entries/${id}`);
      return { success: true, message: 'Time entry deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk update time entries
  bulkUpdate: async (entryIds, updates) => {
    try {
      const response = await apiService.post('time-entries/bulk-update', { entryIds, updates });
      return { success: true, data: response.data, message: 'Time entries updated successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk delete time entries
  bulkDelete: async (entryIds) => {
    try {
      await apiService.post('time-entries/bulk-delete', { entryIds });
      return { success: true, message: 'Time entries deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Export time entries
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`time-entries/export?${queryParams}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Get time entry statistics
  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `time-entries/statistics?${queryParams}` : 'time-entries/statistics';
      const response = await apiService.get(endpoint);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  }
};

export default timeEntryController;